import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Edit3, GripVertical } from "lucide-react";
import { api, imageUrl } from "../../lib/api";

export default function ProjectsList() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragIndex, setDragIndex] = useState(null);
  const [reordering, setReordering] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/projects");
    setProjects(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this project? This cannot be undone.")) return;
    await api.delete(`/projects/${id}`);
    load();
  };

  const onDragStart = (i) => setDragIndex(i);
  const onDragOver = (e, i) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) return;
    const next = [...projects];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(i, 0, moved);
    setProjects(next);
    setDragIndex(i);
  };
  const onDragEnd = async () => {
    if (dragIndex === null) return;
    setDragIndex(null);
    setReordering(true);
    try {
      await api.post("/projects/reorder", { ids: projects.map((p) => p.id) });
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="p-10 max-w-[1280px]" data-testid="admin-projects-list">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#5d5f64] mb-2">Portfolio</div>
          <h1 className="font-display font-semibold text-[#16181c] text-3xl tracking-[-0.02em]">
            Projects
          </h1>
          <p className="text-sm text-[#5d5f64] mt-2">
            Drag the handle to reorder. Order on this list determines the order on the public site.
          </p>
        </div>
        <Link
          to="/admin/projects/new"
          className="inline-flex items-center gap-2 bg-[#16181c] text-white px-5 py-3 font-medium hover:bg-[#5d5f64]"
          data-testid="admin-new-project"
        >
          <Plus size={16} /> New project
        </Link>
      </div>

      {reordering && (
        <div className="text-xs text-[#5d5f64] mb-3">Saving order…</div>
      )}

      {loading ? (
        <div className="text-[#5d5f64]">Loading…</div>
      ) : (
        <div className="bg-white border border-[#e3e3e1]" data-testid="projects-table">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#e3e3e1] text-xs uppercase tracking-wider text-[#5d5f64] items-center">
            <div className="col-span-1"></div>
            <div className="col-span-1">Cover</div>
            <div className="col-span-4">Title</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">Location</div>
            <div className="col-span-1">Year</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {projects.map((p, i) => (
            <div
              key={p.id}
              draggable
              onDragStart={() => onDragStart(i)}
              onDragOver={(e) => onDragOver(e, i)}
              onDragEnd={onDragEnd}
              className={`grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#e3e3e1] items-center group ${
                dragIndex === i ? "bg-[#f3f3f0]" : ""
              }`}
              data-testid={`admin-project-row-${p.id}`}
            >
              <div
                className="col-span-1 flex items-center text-[#5d5f64] cursor-grab active:cursor-grabbing"
                aria-label="Drag to reorder"
                data-testid={`project-drag-handle-${p.id}`}
              >
                <GripVertical size={16} />
              </div>
              <div className="col-span-1">
                <div className="w-12 h-12 bg-[#1b1c1e] overflow-hidden rounded-lg">
                  {p.cover_image && (
                    <img
                      src={imageUrl(p.cover_image)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              </div>
              <div className="col-span-4 text-[#16181c] font-medium min-w-0">
                <div className="truncate">
                  {p.title?.id || p.title?.en || p.name?.id || p.name?.en || "(untitled)"}
                </div>
                <div className="text-xs text-[#5d5f64] mt-1 truncate">
                  /{p.slug || "—"}
                  {p.featured && " · ★ featured"}
                </div>
              </div>
              <div className="col-span-2">
                <div className="text-sm text-[#5d5f64] capitalize">{p.category}</div>
                <span
                  className={`inline-block mt-1 text-[10px] uppercase tracking-wider px-2 py-0.5 ${
                    p.status === "draft"
                      ? "bg-[#f3f3f0] text-[#5d5f64]"
                      : "bg-[#16181c] text-white"
                  }`}
                  data-testid={`project-status-${p.id}`}
                >
                  {p.status || "published"}
                </span>
              </div>
              <div className="col-span-2 text-sm text-[#5d5f64] truncate">{p.location}</div>
              <div className="col-span-1 text-sm text-[#5d5f64]">{p.year}</div>
              <div className="col-span-1 flex justify-end gap-3">
                <Link
                  to={`/admin/projects/${p.id}`}
                  className="text-[#16181c] hover:text-[#5d5f64]"
                  data-testid={`admin-edit-${p.id}`}
                  aria-label="Edit"
                >
                  <Edit3 size={16} />
                </Link>
                <button
                  onClick={() => onDelete(p.id)}
                  className="text-[#16181c] hover:text-[#5d5f64]"
                  data-testid={`admin-delete-${p.id}`}
                  aria-label="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
          {projects.length === 0 && (
            <div className="px-6 py-10 text-center text-[#5d5f64]">No projects yet.</div>
          )}
        </div>
      )}
    </div>
  );
}
